<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';
import * as Sentry from '@sentry/vue';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import ProgressSpinner from 'primevue/progressspinner';
import { useAuthStore } from '@/stores/auth';
import BaseIcon from "@/components/base/BaseIcon.vue";
import LoginPlatformCountrySelect from '@/components/auth/LoginPlatformCountrySelect.vue';
import { prepareAuthRequest } from '@/utils/pkce';
import authApi from '@/api/modules/authApi';

// --- Component Setup ---

const router = useRouter();
const route = useRoute();
const toast = useToast();
const { t } = useI18n();
const authStore = useAuthStore();
const cialLoading = ref(false);
const credentialsLoading = ref(false);
const callbackLoading = ref(false);
const callbackStatusKey = ref('login.callback.finalizing');
const callbackError = ref('');
const showDevOptions = ref(true);

const devForm = reactive({
  email: '',
  password: ''
});

// --- Methods ---

const clearCialFlowStorage = (): void => {
  sessionStorage.removeItem('cial_auth_state');
  sessionStorage.removeItem('cial_auth_code_verifier');
  sessionStorage.removeItem('cial_auth_nonce');
};

const handleCialCallbackError = (message: string): void => {
  callbackError.value = message;
  callbackLoading.value = false;
  if (route.query.code || route.query.state) {
    void router.replace({ name: 'login' });
  }
  toast.add({
    severity: 'error',
    summary: t('login.toast.loginFailedTitle'),
    detail: message,
    life: 5000
  });
};

const handleCialCallback = async (): Promise<void> => {
  const code = route.query.code as string | undefined;
  const state = route.query.state as string | undefined;

  if (!code && !state) {
    return;
  }

  callbackLoading.value = true;
  callbackError.value = '';
  callbackStatusKey.value = 'login.callback.validating';

  if ( !code || !state) {
    clearCialFlowStorage();
    handleCialCallbackError(t('login.errors.loginIncomplete'));
    return;
  }

  const savedState = sessionStorage.getItem('cial_auth_state');
  const codeVerifier = sessionStorage.getItem('cial_auth_code_verifier');
  const nonce = sessionStorage.getItem('cial_auth_nonce');
  clearCialFlowStorage();

  if (state !== savedState) {
    handleCialCallbackError(t('login.errors.invalidState'));
    return;
  }

  if (!codeVerifier) {
    handleCialCallbackError(t('login.errors.sessionExpired'));
    return;
  }

  try {
    callbackStatusKey.value = 'login.callback.connecting';
    await authStore.loginWithCial(code, codeVerifier, nonce);
    sessionStorage.removeItem('force_cial_reauth');

    const redirectPath = sessionStorage.getItem('cial_post_login_redirect') || '/';
    sessionStorage.removeItem('cial_post_login_redirect');

    toast.add({
      severity: 'success',
      summary: t('login.toast.successTitle'),
      detail: t('login.toast.successDetail'),
      life: 3000
    });

    await router.replace(redirectPath);
  } catch (error) {
    console.error('Cial exchange failed:', error);
    sessionStorage.removeItem('cial_post_login_redirect');
    handleCialCallbackError(t('login.errors.cialAuthFailed'));
  } finally {
    callbackLoading.value = false;
  }
};

const handleDevLogin = async (): Promise<void> => {
  if (!devForm.email || !devForm.password) {
    toast.add({ severity: 'warn', summary: t('login.toast.warningTitle'), detail: t('login.errors.missingFields'), life: 3000 });
    return;
  }

  credentialsLoading.value = true;
  try {
    await authStore.login({
      email: devForm.email,
      password: devForm.password
    });

    toast.add({
      severity: 'success',
      summary: t('login.toast.successTitle'),
      detail: t('login.toast.successDetail'),
      life: 3000
    });

    const redirectPath = (route.query.redirect as string) || '/';
    router.push(redirectPath);
  } catch (error) {
    console.error('Dev login failed:', error);
    toast.add({
      severity: 'error',
      summary: t('login.toast.errorTitle'),
      detail: t('login.errors.invalidCredentials'),
      life: 5000
    });
  } finally {
    credentialsLoading.value = false;
  }
};

const handleCialLogin = async (): Promise<void> => {
  const authUrlParam = authStore.config?.cial_auth_url;
  const clientIdParam = authStore.config?.cial_client_id;

  // Use window.location.origin to ensure the redirect URI always matches the current environment
  const redirectUriParam = `${window.location.origin}/auth/check`;

  if (!authUrlParam || !clientIdParam) {
    console.error('Cial auth config missing from metadata:', authStore.config);
    toast.add({
      severity: 'error',
      summary: t('login.toast.authErrorTitle'),
      detail: t('login.errors.authConfigMissing'),
      life: 5000
    });
    return;
  }

  cialLoading.value = true;
  try {
    const redirectPath = (route.query.redirect as string) || '/';
    sessionStorage.setItem('cial_post_login_redirect', redirectPath);
    const mustForceReauth = sessionStorage.getItem('force_cial_reauth') === '1';
    const additionalAuthParams = mustForceReauth
      ? { prompt: 'login', max_age: '0' }
      : undefined;

    const authUrl = await prepareAuthRequest(
      authUrlParam,
      clientIdParam,
      redirectUriParam,
      'openid profile email',
      additionalAuthParams
    );

    const authState = sessionStorage.getItem('cial_auth_state');
    if (authState) {
      // Best effort: a failed audit call must never block the redirect.
      try {
        await authApi.recordCialLoginAttempt(authState);
      } catch (error: any) {
        console.warn('Login attempt log failed:', error);
        Sentry.captureException(error, {
          tags: { feature: 'login-audit', action: 'record-attempt', http_status: error?.status },
        });
        // The redirect below unloads the page, so send the event before leaving.
        await Sentry.flush(1000);
      }
    }

    // perform the external redirect
    window.location.replace(authUrl);
  } catch (error) {
    console.error('Cial login initiation failed:', error);
    toast.add({
      severity: 'error',
      summary: t('login.toast.errorTitle'),
      detail: t('login.errors.loginStartFailed'),
      life: 5000
    });
    cialLoading.value = false;
  }
};

onMounted(async () => {
  await handleCialCallback();
});
</script>

<template>
  <div class="login-layout flex h-screen w-full bg-surface-0 overflow-hidden">
    <!-- Left Side: Branding/Identity (Hidden on mobile) -->
    <div class="hidden lg:flex flex-col justify-between w-1/2 bg-primary-900 p-12 text-white relative overflow-hidden">
      <!-- Decorative Background Pattern -->
      <div class="absolute inset-0 opacity-10 pointer-events-none">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" stroke-width="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div class="relative z-10">
        <div class="mb-12">
          <img src="/assets/int_one.png" :alt="t('login.logoAlt')" class="h-24 w-auto rounded-full" />
        </div>

        <div class="max-w-lg">
          <i18n-t keypath="login.headline" tag="h1" class="text-5xl font-extrabold tracking-tight mb-6" scope="global">
            <template #highlight>
              <span class="text-primary-400">{{ t('login.headlineHighlight') }}</span>
            </template>
          </i18n-t>
          <p class="text-xl text-primary-100 leading-relaxed opacity-90">
            {{ t('login.subtitle') }}
          </p>
        </div>
      </div>

      <div class="relative z-10 flex items-center gap-4 text-primary-200 text-sm font-medium">
        <div class="flex -space-x-2">
          <div v-for="i in 3" :key="i" class="w-8 h-8 rounded-full border-2 border-primary-900 bg-primary-700 flex items-center justify-center text-[10px]">
            {{ ['AD', 'CM', 'IF'][i-1] }}
          </div>
        </div>
        <span>{{ t('login.socialProof') }}</span>
      </div>
    </div>

    <!-- Right Side: Auth Form -->
    <div class="flex-1 flex flex-col items-center justify-center p-6 sm:p-12 lg:p-24 bg-surface-50 relative">
      <!-- Background Blobs for Visual Interest -->
      <div class="absolute top-0 right-0 w-64 h-64 bg-primary-100 rounded-full blur-3xl opacity-50 -mr-32 -mt-32"></div>
      <div class="absolute bottom-0 left-0 w-96 h-96 bg-primary-50 rounded-full blur-3xl opacity-50 -ml-48 -mb-48"></div>

      <div class="w-full max-w-md relative z-10">
        <!-- Logo for Mobile -->
        <div class="lg:hidden mb-10 flex justify-center">
          <img src="/assets/int_one.png" :alt="t('login.logoAlt')" class="h-16 w-auto rounded-full" />
        </div>

        <div class="bg-surface-0 rounded-3xl shadow-2xl p-8 sm:p-10 border border-surface-100">
          <header class="mb-10">
            <h2 class="text-3xl font-bold text-surface-900 mb-3 tracking-tight">{{ t('login.welcome') }}</h2>
            <!-- <p class="text-surface-500 font-medium">Faça login.</p> -->
          </header>

          <main class="space-y-8">
            <div v-if="callbackLoading" class="rounded-2xl border border-primary-100 bg-primary-50 p-6 text-center space-y-3">
              <div class="flex justify-center">
                <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="4" />
              </div>
              <p class="text-sm font-semibold text-primary-900">{{ t(callbackStatusKey) }}</p>
              <p class="text-xs text-primary-700">{{ t('login.callback.waitNotice') }}</p>
            </div>

            <div v-if="callbackError" class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {{ callbackError }}
            </div>

            <!-- Primary Action: Cial D&B -->
            <div v-if="authStore.isCialLogin" class="space-y-4">
              <button
                @click="handleCialLogin"
                class="auth-primary-btn group relative w-full flex items-center justify-center gap-4 py-4 px-6 rounded-2xl transition-all duration-300 transform active:scale-[0.98]"
                :disabled="callbackLoading || cialLoading || credentialsLoading"
              >
                <div v-if="cialLoading" class="absolute inset-0 flex items-center justify-center bg-inherit rounded-2xl">
                  <BaseIcon class="animate-spin text-xl">progress_activity</BaseIcon>
                </div>
                <template v-else>
                  <BaseIcon class="text-2xl group-hover:scale-110 transition-transform">security</BaseIcon>
                  <span class="text-lg font-bold">{{ t('login.cialButton') }}</span>
                </template>
              </button>

              <p class="text-[11px] text-center text-surface-400 px-4">
                {{ t('login.cialRedirectNotice') }}
              </p>
            </div>

            <!-- Credentials Login Section -->
            <section v-if="!authStore.isCialLogin">
              <div class="flex items-center justify-between w-full text-surface-500 mb-4">
                <span class="text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                  <BaseIcon size="xs">login</BaseIcon>
                  {{ t('login.credentialsSection') }}
                </span>
              </div>

              <div v-if="showDevOptions" class="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                <div class="space-y-3">
                  <div class="flex flex-col gap-1.5">
                    <label class="text-[10px] font-bold text-surface-400 uppercase ml-1">{{ t('login.emailLabel') }}</label>
                    <InputText
                      v-model="devForm.email"
                      placeholder="user@integrityone.com"
                      class="w-full !rounded-xl !border-surface-200 focus:!ring-primary-500"
                      :disabled="callbackLoading || cialLoading || credentialsLoading"
                    />
                  </div>
                  <div class="flex flex-col gap-1.5">
                    <label class="text-[10px] font-bold text-surface-400 uppercase ml-1">{{ t('login.passwordLabel') }}</label>
                    <Password
                      v-model="devForm.password"
                      placeholder="••••••••"
                      class="w-full"
                      :input-class="'w-full !rounded-xl !border-surface-200 focus:!ring-primary-500'"
                      :disabled="callbackLoading || cialLoading || credentialsLoading"
                      :feedback="false"
                      toggleMask
                    />
                  </div>
                </div>

                <button
                  @click="handleDevLogin"
                  class="auth-primary-btn group relative w-full flex items-center justify-center gap-4 py-4 px-6 rounded-2xl transition-all duration-300 transform active:scale-[0.98]"
                  :disabled="callbackLoading || cialLoading || credentialsLoading"
                >
                  <div v-if="credentialsLoading" class="absolute inset-0 flex items-center justify-center bg-inherit rounded-2xl">
                    <BaseIcon class="animate-spin text-xl">progress_activity</BaseIcon>
                  </div>
                  <template v-else>
                    <BaseIcon class="text-2xl group-hover:scale-110 transition-transform">badge</BaseIcon>
                    <span class="text-lg font-bold">{{ t('login.submit') }}</span>
                  </template>
                </button>
              </div>
            </section>
          </main>
        </div>

        <!-- Below the card, Google-style: platform country on the left, environment info on the right -->
        <footer class="mt-4 px-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <LoginPlatformCountrySelect />
          <p class="text-[10px] text-surface-400 font-medium">
            {{ t('login.footer') }}
          </p>
        </footer>
      </div>
    </div>
  </div>
</template>

<style scoped>
.login-layout {
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}

.auth-primary-btn {
  background: linear-gradient(135deg, #002D72 0%, #0046ad 100%);
  color: white;
  box-shadow: 0 10px 20px -5px rgba(0, 45, 114, 0.3);
}

.auth-primary-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #001a4d 0%, #002D72 100%);
  box-shadow: 0 15px 30px -10px rgba(0, 45, 114, 0.4);
  transform: translateY(-1px);
}

.auth-primary-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  filter: grayscale(0.5);
}

:deep(.p-password-input) {
  width: 100% !important;
}

.animate-in {
  animation-fill-mode: forwards;
}

@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slide-in-from-top-2 {
  from { transform: translateY(-0.5rem); }
  to { transform: translateY(0); }
}

.fade-in { animation-name: fade-in; }
.slide-in-from-top-2 { animation-name: slide-in-from-top-2; }
</style>
